import { useMemo } from 'react';
import { useNewsletterSubscribers } from '../../../core/api/hooks/useNewsletter';
import { AdminTable, Column } from '../components/shared/AdminTable';
import { PageHeader } from '../components/shared/PageHeader';
import { format } from 'date-fns';
import clsx from 'clsx';
import { CheckCircle, XCircle } from 'lucide-react';
import { useAdminT } from '../../../hooks/useAdminT';

interface Subscriber {
    id: string;
    email: string;
    active: boolean;
    createdAt: string;
}

export const NewsletterPage = () => {
    const { t } = useAdminT();
    const { data, isLoading } = useNewsletterSubscribers();

    const columns = useMemo<Column<Subscriber>[]>(() => [
        {
            header: t("inquiries.fields.email"),
            accessorKey: 'email',
            cell: (item) => <span className="font-medium text-slate-900">{item.email}</span>,
        },
        {
            header: t("common.status"),
            accessorKey: 'active',
            cell: (item) => (
                <span className={clsx(
                    "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium",
                    item.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                )}>
                    {item.active ? (
                        <>
                            <CheckCircle className="w-3.5 h-3.5" />
                            {t("common.active")}
                        </>
                    ) : (
                        <>
                            <XCircle className="w-3.5 h-3.5" />
                            {t("common.inactive")}
                        </>
                    )}
                </span>
            ),
        },
        {
            header: t("common.createdAt"),
            accessorKey: 'createdAt',
            cell: (item) => <span className="text-sm text-slate-500">{format(new Date(item.createdAt), 'MMM dd, yyyy HH:mm')}</span>,
        },
    ], [t]);

    return (
        <div className="p-6 max-w-[1600px] mx-auto space-y-6">
            <PageHeader
                title={t("newsletter.title")}
                description={t("newsletter.subtitle")}
            />

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <AdminTable
                    data={data || []}
                    columns={columns}
                    isLoading={isLoading}
                    searchPlaceholder={t("newsletter.searchPlaceholder")}
                />
            </div>
        </div>
    );
};

export default NewsletterPage;
